import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ColumnValidationError } from '../intake.api';

export interface ValidationPanelProps {
  errors: ColumnValidationError[];
}

/** Een waarschuwing wordt getoond maar houdt het indienen niet tegen (h7-1). */
export function isBlokkerend(error: ColumnValidationError): boolean {
  return error.level !== 'warning';
}

/**
 * h7-1: de BFF stuurt veldsleutels, geen veldnamen — anders leest een Engelstalige klant een
 * Nederlands veld midden in een Engelse zin. Hier krijgen ze hun naam in de taal van het scherm.
 * Een sleutel zonder vertaling (de GS1-productvelden, FR48) blijft staan zoals hij binnenkwam.
 */
export function metVeldnamen(t: TFunction, params: ColumnValidationError['params']): Record<string, string | number> | undefined {
  if (!params) return params;
  const naam = (sleutel: string | number) =>
    t(`intake.review.validation.field.${sleutel}`, { defaultValue: String(sleutel) });
  return {
    ...params,
    ...(params.field === undefined ? {} : { field: naam(params.field) }),
    ...(params.other === undefined ? {} : { other: naam(params.other) }),
  };
}

/** FR32: inline, herstelbaar — never a rejected upload or a raw technical error code list. */
/**
 * Welke tekst het vóór de melding zet. Een bevinding die over het hele artikel gaat — geen enkel
 * verpakkingsmateriaal, of te weinig composietlagen — draagt geen groepsnummer. Zonder deze keuze
 * kwam `{{group}}` letterlijk op het scherm; gezien bij een beproeving op acceptatie 12-09-2026.
 */
function plaatsSleutel(regio: string | number, groep: unknown): string {
  const basis = `intake.review.validation.place.${regio}`;
  return groep === undefined || groep === null ? `${basis}Alles` : basis;
}

export function ValidationPanel({ errors }: ValidationPanelProps) {
  const { t } = useTranslation();

  if (errors.length === 0) return null;

  const blokkerend = errors.some(isBlokkerend);

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="intake-validation-panel"
      className={`rounded-[var(--radius-card)] border p-3 ${blokkerend ? 'border-[var(--destructive)] bg-[var(--destructive-light)]' : 'border-[var(--border)] bg-[var(--background-muted)]'}`}
    >
      <p className={`mb-2 text-sm font-semibold ${blokkerend ? 'text-[var(--destructive)]' : 'text-[var(--foreground-heading)]'}`}>
        {t(blokkerend ? 'intake.review.validation.title' : 'intake.review.validation.warningTitle')}
      </p>
      <ul className="flex flex-col gap-1">
        {errors.map((error, index) => (
          <li
            key={index}
            data-testid={`intake-validation-error-${error.code}`}
            className={`text-sm ${isBlokkerend(error) ? 'text-[var(--destructive)]' : 'text-[var(--foreground-muted)]'}`}
          >
            {/* h7-1: verpakkingsmeldingen dragen een rijnummer en een groep. Die staan vóór de tekst,
                zodat de klant eerst weet wáár hij moet zijn en dan pas wat er mis is. */}
            {error.params?.region
              ? `${t(plaatsSleutel(error.params.region, error.params.group), error.params)} `
              : ''}
            {t(`intake.review.validation.errors.${error.code}`, metVeldnamen(t, error.params))}
          </li>
        ))}
      </ul>
    </div>
  );
}
